import React, { useState, useEffect } from 'react';
import {
    Container, Grid, TextField, MenuItem, Button, Paper, Typography, Snackbar, Alert, Dialog, Box,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button as MuiButton,
    IconButton,
    Tooltip,
    Chip,
    Modal,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import SaveIcon from '@mui/icons-material/Save';

const CreateTimeTab = () => {
    const [classes, setClasses] = useState([]);
    const [classRoom, setClassRoom] = useState('0');
    const [section, setSection] = useState('0');
    const [sections, setSections] = useState([]);
    const [teachersList, setTeacherList] = useState([]);
    const [selectedTeacherId, setSelectedTeacherId] = useState('');
    const [subjects, setSubjects] = useState([]);
    const [periods, setPeriods] = useState([]);
    const [daysOfWeek, setDaysOfWeek] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [formData, setFormData] = useState({
        dayOfWeek: '',
        classId: '',
        sectionId: '',
        subjectId: '',
        teacherId: '',
        roomNo: '',
        periodSequence: '',
    });
    const navigate = useNavigate();
    const [isFormCleared, setIsFormCleared] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');

    // State for Info Modal
    const [infoModalOpen, setInfoModalOpen] = useState(false);

    const handleDialogClose = () => {
        setDialogOpen(false);
        setDialogMessage('');
    };

    const showDialog = (message) => {
        setDialogMessage(message);
        setDialogOpen(true);
    };

    const checkTeacherSlot = async (teacherId, daysId, periodId) => {
    try {
        const apiUrl = "https://wsmsv1.aidigitalsec.com/api/TimeTable/Check_TeacherSlot";
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: sessionStorage.getItem('token'),
            },
            body: JSON.stringify({
                teacherId: parseInt(teacherId, 10),
                daysId: parseInt(daysId, 10),
                periodId: parseInt(periodId, 10),
            }),
        });

        if (!response.ok) {
            throw new Error(`Error checking teacher slot: ${response.status}`);
        }

        const data = await response.json();
        return data.msg === "Record Not Found"; // Return true if no conflict, false if conflict exists
    } catch (error) {
        console.error('Error checking teacher slot:', error);
        return false; // Assume conflict on error
    }
};

    const fetchClasses = async () => {
        try {
            const apiUrl = process.env.REACT_APP_BASE_URL;
            const response = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: sessionStorage.getItem('token'),
                },
                body: JSON.stringify({}),
            });
            const data = await response.json();
            if (data.msg === "Record Not Found") {
                showDialog("Record Not Found");
                setClasses([]); // Reset classes to an empty array
                return;
            }
            setClasses(data);
        } catch (error) {
            console.error('Error fetching classes:', error);
        }
    };

    const fetchSections = async (classId) => {
        try {
            const apiUrl = process.env.REACT_APP_BASE_URL;
            const response = await fetch(`${apiUrl}/Teacher/ddlSection_clsId`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: sessionStorage.getItem('token'),
                },
                body: JSON.stringify({
                    teacherId: sessionStorage.getItem('employeeId'),
                    classId,
                }),
            });
            const data = await response.json();
            if (data.msg === "Record Not Found") {
                showDialog("Record Not Found");
                setSections([]); // Reset sections to an empty array
                return;
            }
            setSections(data);
        } catch (error) {
            console.error('Error fetching sections:', error);
        }
    };

    const fetchTeachers = async () => {
        try {
            const apiUrl = process.env.REACT_APP_BASE_URL;
            const response = await fetch(`${apiUrl}/AssignmentCreate/GetTeacher`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: sessionStorage.getItem('token'),
                },
                body: JSON.stringify({}),
            });
            const data = await response.json();
            if (data.msg === "Record Not Found") {
                showDialog("Record Not Found");
                setTeacherList([]); // Reset teacher list
                return;
            }
            setTeacherList(data);
        } catch (error) {
            console.error('Error fetching teachers:', error);
        }
    };

    const fetchSubjects = async (classId, sectionId, teacherId) => {
        try {
            const apiUrl = process.env.REACT_APP_BASE_URL;
            const response = await fetch(`${apiUrl}/AssignmentCreate/Subjectddl`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: sessionStorage.getItem('token'),
                },
                body: JSON.stringify({
                    classId,
                    sectionId,
                    teacherId,
                }),
            });
            const data = await response.json();
            if (data.msg === "Record Not Found") {
                showDialog("Record Not Found");
                setSubjects([]); // Reset subjects
                return;
            }
            setSubjects(data);
        } catch (error) {
            console.error('Error fetching subjects:', error);
        }
    };

    const fetchPeriods = async () => {
        try {
            const apiUrl = "https://wsmsv1.aidigitalsec.com/api/TimeTable/GetPeriod";
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: sessionStorage.getItem('token'), // Include token if needed
                },
                body: JSON.stringify({}), // Adjust body if required by API
            });

            if (!response.ok) {
                throw new Error(`Error fetching periods: ${response.status}`);
            }

            const data = await response.json();
            setPeriods(data); // Assuming data is an array of periods
        } catch (error) {
            console.error('Error fetching periods:', error);
        }
    };

    const fetchDaysOfWeek = async () => {
        try {
            console.log("Fetching Days of Week...");
            const response = await fetch("https://wsmsv1.aidigitalsec.com/api/TimeTable/GetDaysOfWeek", {
                method: 'POST', // or 'GET' depending on the API
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: sessionStorage.getItem('token'), // Add token if necessary
                },
                body: JSON.stringify({}), // Adjust body if required by API
            });

            if (!response.ok) {
                throw new Error(`Error fetching days of the week: ${response.status}`);
            }

            const data = await response.json();
            console.log("Days of Week data:", data);
            setDaysOfWeek(data); // Store the response data
        } catch (error) {
            console.error("Error fetching days of the week:", error);
        }
    };



    useEffect(() => {
        fetchClasses();
        fetchTeachers();
        fetchPeriods();
        fetchDaysOfWeek();
    }, []);

    useEffect(() => {
        if (classRoom !== '0' && section !== '0' && selectedTeacherId !== '') {
            fetchSubjects(classRoom, section, selectedTeacherId);
        }
    }, [classRoom, section, selectedTeacherId]);

    const handleClassChange = (event) => {
        const selectedClassId = event.target.value;
        setClassRoom(selectedClassId);
        setSection('0'); // Reset section and subjects when class changes
        setSubjects([]);
        fetchSections(selectedClassId); // Fetch sections based on selected class ID
    };

    const handleSectionChange = (event) => {
        setSection(event.target.value);
    };

    const handleTeacherChange = (event) => {
        setSelectedTeacherId(event.target.value);
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleCancel = () => {
        const isFilled = Object.values(formData).some(value => value !== ''); // Check if any field has data

        if (!isFilled) {
            navigate(-1); // If already clear, navigate immediately
        } else {
            // Clear the form first
            setFormData({
                dayOfWeek: '',
                classId: '',
                sectionId: '',
                subjectId: '',
                teacherId: '',
                roomNo: '',
                periodSequence: '',
            });
            setClassRoom('0');
            setSection('0');
            setSelectedTeacherId('');
            setSubjects([]);
            setIsFormCleared(true); // Mark form as cleared

            // After the first click, setTimeout ensures that on the next click it navigates back
            setTimeout(() => setIsFormCleared(false), 0);
        }
    };

   const handleSubmit = async () => {
    const timetableEntry = {
        classId: parseInt(classRoom, 10),
        sectionId: parseInt(section, 10),
        subjectId: parseInt(formData.subjectId, 10),
        teacherId: parseInt(selectedTeacherId, 10),
        roomNo: parseInt(formData.roomNo || 0, 10),
        dayOfWeekId: formData.dayOfWeek,
        periodId: parseInt(formData.periodSequence)
    };

    // Check for duplicate teacher slot
    const isSlotAvailable = await checkTeacherSlot(selectedTeacherId, formData.dayOfWeek, formData.periodSequence);
    if (!isSlotAvailable) {
        setSnackbar({ open: true, message: 'Teacher already has a class during this slot!', severity: 'error' });
        return;
    }

    try {
        const apiUrl = process.env.REACT_APP_BASE_URL;
        const response = await fetch(`${apiUrl}/TimeTable/CreateTimeTable`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: sessionStorage.getItem('token'),
            },
            body: JSON.stringify([timetableEntry]),
        });

        if (!response.ok) {
            throw new Error(`Error submitting data: ${response.status}`);
        }

        const result = await response.json();
        if (result?.msg === "Details Saved successfully!") {
            setSnackbar({ open: true, message: result.msg, severity: 'success' });
            handleCancel();
        } else if (result?.msg === "Time Table already exist during given time!") {
            setSnackbar({ open: true, message: result.msg, severity: 'error' });
        }
    } catch (error) {
        console.error('Error submitting timetable:', error);
        setSnackbar({ open: true, message: 'Error saving details!', severity: 'error' });
    }
};

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Info sections data for the Info Modal
    const infoSections = [
        {
            icon: <CalendarTodayIcon sx={{ color: "#1976d2", fontSize: 22 }} />,
            title: "Schedule Fields",
            color: "#e3f2fd",
            borderColor: "#1976d2",
            items: [
                { label: "Days of Week", desc: "Select the day on which this timetable slot applies (e.g. Monday, Tuesday)." },
                { label: "Period Sequence", desc: "Select the period slot for this entry. Periods are defined in the Time Period management page." },
                { label: "Room No", desc: "Enter the classroom or room number where this class will be held. Optional field (defaults to 0)." },
            ],
        },
        {
            icon: <SchoolIcon sx={{ color: "#7b1fa2", fontSize: 22 }} />,
            title: "Class & Subject Fields",
            color: "#f3e5f5",
            borderColor: "#7b1fa2",
            items: [
                { label: "Class", desc: "Select the class for this timetable entry. Changing class resets the section and subject dropdowns." },
                { label: "Section", desc: "Select the section within the chosen class. Loaded automatically after selecting a class." },
                { label: "Subject", desc: "Select the subject for this slot. Loaded automatically once Class, Section, and Teacher are all selected." },
            ],
        },
        {
            icon: <PersonIcon sx={{ color: "#e65100", fontSize: 22 }} />,
            title: "Teacher & Conflict Check",
            color: "#fff3e0",
            borderColor: "#e65100",
            items: [
                { label: "Teacher", desc: "Select the teacher assigned to this slot. Subject options update based on the selected teacher." },
                { label: "Slot Conflict", desc: "Before saving, the system checks if the selected teacher is already assigned to another class during the same day and period." },
                { label: "Duplicate Slot", desc: "If a timetable entry already exists for the same time slot, the system will reject the submission and show an error." },
            ],
        },
        {
            icon: <SaveIcon sx={{ color: "#2e7d32", fontSize: 22 }} />,
            title: "Actions",
            color: "#e8f5e9",
            borderColor: "#2e7d32",
            items: [
                { label: "ADD", desc: "Submits the timetable entry after validating for teacher slot conflicts and existing duplicates." },
                { label: "CANCEL", desc: "First click clears the form. Second click (if form is empty) navigates back to the previous page." },
            ],
        },
    ];

    return (
        <Container maxWidth="md">
            <Box
                sx={{
                    backgroundColor: '#0B1F40', // dark navy blue
                    padding: '12px 24px',
                    boxShadow: 3,
                    borderRadius: '4px 4px 0 0',
                    margin: '2px auto',
                    maxWidth: "900px",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Typography variant="h4" sx={{ color: '#fff', textAlign: 'center', fontWeight: 'bold', flex: 1 }}>
                    Create Time-Table
                </Typography>

                {/* Info Button */}
                <Tooltip title="View page information & guide" arrow placement="left">
                    <IconButton
                        onClick={() => setInfoModalOpen(true)}
                        size="small"
                        sx={{
                            color: "rgba(255,255,255,0.75)",
                            backgroundColor: "rgba(255,255,255,0.1)",
                            border: "1.5px solid rgba(255,255,255,0.25)",
                            borderRadius: "50%",
                            width: 32,
                            height: 32,
                            transition: "all 0.2s ease",
                            "&:hover": {
                                backgroundColor: "#1976d2",
                                borderColor: "#64B5F6",
                                color: "#ffffff",
                                boxShadow: "0 0 0 3px rgba(100,181,246,0.25)",
                                transform: "scale(1.1)",
                            },
                        }}
                    >
                        <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            </Box>

            <Paper elevation={3} style={{ padding: 16, paddingTop:34, width: "100%", margin: "auto", marginTop: 6, marginBottom: 6 }}>

                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            fullWidth
                            label="Days of Week"
                            name="dayOfWeek"
                            value={formData.dayOfWeek || ''}
                            onChange={handleChange}
                            variant="outlined"
                        >
                            <MenuItem value=""><em>Select</em></MenuItem>
                            {daysOfWeek.map((day) => (
                                <MenuItem key={day.dayId} value={day.dayId}>
                                    {day.dayName}  {/* Display dayName */}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            fullWidth
                            name="class"
                            value={classRoom}
                            label="Class"
                            onChange={handleClassChange}
                        >
                            <MenuItem value="0"><em>Select</em></MenuItem>
                            {classes.map((classItem) => (
                                <MenuItem key={classItem.classId} value={classItem.classId}>
                                    {classItem.className}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            fullWidth
                            name="section"
                            value={section}
                            label="Section"
                            onChange={handleSectionChange}
                            variant="outlined"
                        >
                            <MenuItem value="0"><em>Select</em></MenuItem>
                            {sections.map((sectionItem) => (
                                <MenuItem key={sectionItem.sectionId} value={sectionItem.sectionId}>
                                    {sectionItem.sectionName}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            fullWidth
                            label="Teacher"
                            name="teacherId"
                            value={selectedTeacherId}
                            onChange={handleTeacherChange}
                            variant="outlined"
                        >
                            <MenuItem value=""><em>Select</em></MenuItem>
                            {teachersList.map((item) => (
                                <MenuItem key={item.employeeId} value={item.employeeId}>
                                    {item.employeeName}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            fullWidth
                            label="Subject"
                            name="subjectId"
                            value={formData.subjectId}
                            onChange={handleChange}
                            variant="outlined"
                        >
                            <MenuItem value=""><em>Select</em></MenuItem>
                            {subjects.map((subject) => (
                                <MenuItem key={subject.subjectId} value={subject.subjectId}>
                                    {subject.subjectName}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Room No"
                            name="roomNo"
                            value={formData.roomNo}
                            onChange={handleChange}
                            type="number"
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            fullWidth
                            label="Period Sequence"
                            name="periodSequence"
                            value={formData.periodSequence || ''}
                            onChange={handleChange}
                            variant="outlined"
                        >
                            <MenuItem value=""><em>Select</em></MenuItem>
                            {periods.map((period) => (
                                <MenuItem key={period.periodId} value={period.periodId}>
                                    {`Period ${period.sequenceNo}`}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>


                    <Grid item xs={12} display="flex" justifyContent="flex-end">
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{
                                mr: 1, width: "90px",
                                height: "50px",
                                padding: "10px 0",
                                fontWeight: 'bold'
                            }}
                            onClick={handleSubmit} // Trigger handleSubmit on click
                        >
                            ADD
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleCancel}
                            sx={{  width: "90px",
              height: "50px",
              padding: "10px 0",
              fontWeight:'bold'
 }}
                        >
                            CANCEL
                        </Button>
                    </Grid>
                </Grid>
            </Paper>
            {/* Snackbar for messages */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000} // Closes after 4 seconds
                onClose={() => setSnackbar({ ...snackbar, open: false })} // Ensure closing works
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <Dialog
                open={dialogOpen}
                onClose={handleDialogClose}
                aria-labelledby="dialog-title"
                aria-describedby="dialog-description"
                maxWidth="sm"  // Options are "xs", "sm", "md", "lg", "xl"
                fullWidth={true}  // Expands dialog to the maximum width defined by maxWidth
            >
                <DialogTitle id="dialog-title">Alert</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">{dialogMessage}</Typography>
                </DialogContent>
                <DialogActions>
                    <MuiButton onClick={handleDialogClose} color="primary">
                        OK
                    </MuiButton>
                </DialogActions>
            </Dialog>

            {/* Info Modal */}
            <Modal
                open={infoModalOpen}
                onClose={() => setInfoModalOpen(false)}
                aria-labelledby="info-modal-title"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: "95vw", sm: 580, md: 640 },
                        maxHeight: "85vh",
                        bgcolor: "background.paper",
                        borderRadius: 3,
                        boxShadow: "0 24px 64px rgba(11,31,61,0.22)",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {/* Info Modal Header */}
                    <Box
                        sx={{
                            background: "linear-gradient(135deg, #0B1F40 0%, #1565C0 100%)",
                            padding: "16px 20px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexShrink: 0,
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Box
                                sx={{
                                    backgroundColor: "rgba(255,255,255,0.15)",
                                    borderRadius: "50%",
                                    width: 36,
                                    height: 36,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <InfoOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
                            </Box>
                            <Box>
                                <Typography
                                    id="info-modal-title"
                                    variant="h6"
                                    sx={{ color: "#fff", fontWeight: 700, lineHeight: 1.2 }}
                                >
                                    Create Time-Table — Guide
                                </Typography>
                                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>
                                    Complete reference for all fields and actions
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton
                            onClick={() => setInfoModalOpen(false)}
                            sx={{
                                color: "#fff",
                                "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Info Modal Scrollable Body */}
                    <Box sx={{ overflowY: "auto", p: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>

                        {/* Page Overview */}
                        <Box
                            sx={{
                                backgroundColor: "#f0f4ff",
                                border: "1px solid #c5d3f0",
                                borderRadius: 2,
                                p: 2,
                            }}
                        >
                            <Typography variant="body2" sx={{ color: "#1a237e", lineHeight: 1.7 }}>
                                The <strong>Create Time-Table</strong> page lets you assign a subject and teacher to a specific class, section, period, and day. The system automatically checks for scheduling conflicts before saving.
                            </Typography>
                        </Box>

                        {/* Sections */}
                        {infoSections.map((section, sIdx) => (
                            <Box
                                key={sIdx}
                                sx={{
                                    backgroundColor: section.color,
                                    border: `1px solid ${section.borderColor}30`,
                                    borderLeft: `4px solid ${section.borderColor}`,
                                    borderRadius: 2,
                                    p: 2,
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                                    {section.icon}
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1a1a2e", fontSize: "0.95rem" }}>
                                        {section.title}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
                                    {section.items.map((item, iIdx) => (
                                        <Box key={iIdx} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                                            <Chip
                                                label={item.label}
                                                size="small"
                                                sx={{
                                                    fontSize: "0.7rem",
                                                    fontWeight: 600,
                                                    height: 22,
                                                    flexShrink: 0,
                                                    backgroundColor: `${section.borderColor}18`,
                                                    color: section.borderColor,
                                                    border: `1px solid ${section.borderColor}40`,
                                                    mt: "1px",
                                                }}
                                            />
                                            <Typography variant="body2" sx={{ color: "#424242", lineHeight: 1.5, fontSize: "0.82rem" }}>
                                                {item.desc}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        ))}

                        {/* Tip Box */}
                        <Box
                            sx={{
                                backgroundColor: "#fffde7",
                                border: "1px solid #f9a825",
                                borderRadius: 2,
                                p: 2,
                                display: "flex",
                                gap: 1.5,
                                alignItems: "flex-start",
                            }}
                        >
                            <Typography sx={{ fontSize: "1.1rem", mt: "1px" }}>💡</Typography>
                            <Typography variant="body2" sx={{ color: "#5d4037", lineHeight: 1.65, fontSize: "0.82rem" }}>
                                <strong>Pro Tip:</strong> Select <em>Class</em>, <em>Section</em>, and <em>Teacher</em> first — the Subject dropdown will populate automatically based on these three selections. Make sure periods are configured in the Time Period page before creating timetable entries.
                            </Typography>
                        </Box>

                    </Box>

                    {/* Info Modal Footer */}
                    <Box
                        sx={{
                            borderTop: "1px solid #e0e0e0",
                            px: 2.5,
                            py: 1.5,
                            display: "flex",
                            justifyContent: "flex-end",
                            flexShrink: 0,
                            backgroundColor: "#fafafa",
                        }}
                    >
                        <Button
                            onClick={() => setInfoModalOpen(false)}
                            variant="contained"
                            color="primary"
                            sx={{ fontWeight: 600, px: 3 }}
                        >
                            Got It
                        </Button>
                    </Box>
                </Box>
            </Modal>

        </Container>
    );
};

export default CreateTimeTab;